import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { getNewCustomerReportService } from "../../../services/report/services";
import { useTranslator } from "../../../context/translator";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ReportFilters from "../../reportFilters/ReportFilters";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const getLocalISOString = (date) => {
  const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  const localTime = new Date(date.getTime() - timezoneOffsetMs);
  return localTime.toISOString().split("T")[0]; // Return just the date
};

const NewCustomerReport = () => {
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();
  const [data, setData] = useState([]);
  const [textColor, setTextColor] = useState("black");
  const [category, setCategory] = useState("all");
  const [temporality, setTemporality] = useState("all");

  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  const [date1, setDate1] = useState(getLocalISOString(oneYearAgo));
  const [date2, setDate2] = useState(getLocalISOString(today));

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setTextColor(mediaQuery.matches ? "white" : "black");

    const handleChange = (e) => {
      setTextColor(e.matches ? "white" : "black");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const getData = useCallback(async () => {
    const result = await getNewCustomerReportService({
      companyId: company?._id,
      dateFrom: date1,
      dateTo: date2,
      category,
      temporality,
    });

    setData(result || []);
  }, [category, date1, date2, temporality, company]);

  useEffect(() => {
    getData();
  }, [getData]);

  const monthsLabels = [
    translate("jan"),
    translate("feb"),
    translate("mar"),
    translate("apr"),
    translate("may"),
    translate("jun"),
    translate("jul"),
    translate("aug"),
    translate("sep"),
    translate("oct"),
    translate("nov"),
    translate("dec"),
  ];

  const groupedByYear = {};
  data.forEach((item) => {
    if (!groupedByYear[item.year]) {
      groupedByYear[item.year] = Array(12).fill(0);
    }
    groupedByYear[item.year][item.month - 1] = item.count;
  });

  const datasets = Object.entries(groupedByYear).map(
    ([year, monthlyCounts]) => ({
      label: year,
      data: monthlyCounts,
      backgroundColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
        Math.random() * 255,
      )}, ${Math.floor(Math.random() * 255)}, 0.6)`,
    }),
  );

  const chartData = {
    labels: monthsLabels,
    datasets,
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: textColor,
        },
      },
      title: {
        display: true,
        text: translate("new-customers-by-month"),
        color: textColor,
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
        },
        grid: {
          color:
            textColor === "white"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
        },
      },
      y: {
        ticks: {
          color: textColor,
        },
        grid: {
          color:
            textColor === "white"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)",
        },
      },
    },
  };

  const handleGeneratePDF = () => {
    const doc = new jsPDF();

    doc.text(translate("new-customer-report"), 14, 15);

    autoTable(doc, {
      startY: 20,
      head: [
        [
          "#",
          translate("year"),
          translate("month"),
          translate("new-customers"),
        ],
      ],
      body: data.map((item, index) => [
        index + 1,
        item.year,
        monthsLabels[item.month - 1],
        item.count,
      ]),
    });

    doc.save("new-customer-report.pdf");
  };

  if (userLoading || companyLoading) return null;

  return (
    <div className="p-4">
      <div className="flex justify-between">
        <h2 className="text-xl font-semibold mb-4 dark:text-white">
          {translate("new-customer-report")}
        </h2>

        <button
          onClick={handleGeneratePDF}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {translate("download-PDF")}
        </button>
      </div>
      <ReportFilters
        category={category}
        setCategory={setCategory}
        setDateFrom={setDate1}
        setDateTo={setDate2}
        setTemporality={setTemporality}
        temporality={temporality}
        hideTerm={true}
        hideDate={false}
        key={"newCustomerReportFilters"}
      />
      <div className="mb-4">
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default NewCustomerReport;
